/**
 * usePublicComparisonLink.js : chargement de la page du lien de comparaison (D257).
 *
 * Séances du lien, puis toutes les pages de mesures de chacune (D235), avant
 * tout affichage. `loaded` a la forme de useCompareSessions
 * ([{ session, readings, pagination }]) pour nourrir CompareSessionsView et
 * son PDF ; le nom du cheval est posé là où la comparaison le lit
 * (session.horse_id.horseName). Toute erreur d'une route publique remplace la
 * page par un message, sans aucune donnée.
 */
import { useEffect, useState } from 'react';
import { fetchComparisonReadings, fetchLinkedComparison } from './comparisonLinkService';

const EMPTY = { status: 'loading', errorCode: null, payload: null, loaded: [] };

/** Séance publique → session de la comparaison. */
export function adaptComparedSession(entry) {
  return { ...entry.session, horse_id: { horseName: entry.horse?.horseName ?? null } };
}

export function usePublicComparisonLink(token) {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!token) {
      setState({ ...EMPTY, status: 'error', errorCode: 'LINK_NOT_FOUND' });
      return undefined;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setState(EMPTY);
    (async () => {
      try {
        const payload = await fetchLinkedComparison(token, signal);
        const results = await Promise.all(payload.sessions.map((_, rank) => fetchComparisonReadings(token, rank, signal)));
        if (signal.aborted) return;
        const loaded = payload.sessions.map((entry, rank) => ({
          session: adaptComparedSession(entry),
          readings: results[rank].readings,
          pagination: { paginated: results[rank].capped, total: results[rank].total, limit: results[rank].readings.length },
        }));
        setState({ status: 'ready', errorCode: null, payload, loaded });
      } catch (err) {
        if (signal.aborted || err?.name === 'AbortError') return;
        setState({ ...EMPTY, status: 'error', errorCode: err?.code || 'SERVER_ERROR' });
      }
    })();
    return () => controller.abort();
  }, [token]);

  return state;
}
